import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header.jsx';
import Footer from '@/components/Footer.jsx';
import ConverterApp from '@/components/ConverterApp.jsx';
import AdSense from '@/components/AdSense.jsx';
import ShareButtons from '@/components/ShareButtons.jsx';
import SEOHead from '@/components/SEOHead.jsx';
import { generateToolApplicationSchema, generateFAQSchema, generateBreadcrumbSchema } from '@/utils/seoHelpers.js';
import { generateOrganizationSchema, generateWebSiteSchema } from '@/components/StructuredData.jsx';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { getTranslatedCategories } from '@/utils/conversionDataI18n.js';
import { ArrowRight, Zap, Ruler, Cog, Thermometer, Droplet, Sun, Magnet, Radiation, Boxes, Coins } from 'lucide-react';
import { useI18n } from '@/i18n/index.jsx';

function HomePage() {
  const { lang, dict } = useI18n();
  const isEN = lang === 'en';
  const categories = useMemo(() => getTranslatedCategories(dict, lang), [dict, lang]);

  const categoryIcons = {
    common: Ruler,
    engineering: Cog,
    heat: Thermometer,
    fluids: Droplet,
    light: Sun,
    electricity: Zap,
    magnetism: Magnet,
    radiology: Radiation,
    other: Boxes,
    currency: Coins
  };

  const totalConverters = useMemo(
    () => categories.reduce((sum, cat) => sum + cat.converters.length, 0),
    [categories]
  );

  const title = isEN ? 'TudoConversor | The Most Complete Unit Converter' : 'TudoConversor | O Conversor de Unidades Mais Completo';
  const description = isEN 
    ? `Convert units of length, weight, temperature, area and over ${totalConverters} converters at TudoConversor. Free, fast tool with scientific precision.` 
    : `Converta unidades de comprimento, peso, temperatura, área e mais de ${totalConverters} conversores no TudoConversor. Ferramenta gratuita, rápida e com precisão científica.`;
  const h1 = isEN ? 'The Most Complete Unit Converter' : 'O Conversor de Unidades Mais Completo';
  const content = isEN 
    ? `Accurate, instant, and free calculations. Support for over ${totalConverters} converters covering physics, engineering, computing, and everyday measurements.` 
    : `Cálculos precisos, instantâneos e gratuitos. Suporte para mais de ${totalConverters} conversores abrangendo física, engenharia, informática e medidas do dia a dia.`;

  const toolSchema = generateToolApplicationSchema(
    title,
    description,
    "UtilityApplication",
    "https://tudoconversor.com.br"
  );

  const orgSchema = generateOrganizationSchema();
  const webSiteSchema = generateWebSiteSchema();

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [toolSchema, orgSchema, webSiteSchema]
  };

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: isEN ? 'Home' : 'Início', url: "https://tudoconversor.com.br" }
  ]);

  const faqs = [
    { 
      question: isEN ? 'How to convert units on TudoConversor?' : 'Como converter unidades no TudoConversor?', 
      answer: isEN ? 'To convert, just choose the category, select input and output units in the dropdowns, and type the number. The result is displayed in real-time.' : 'Para converter, basta escolher a categoria, selecionar as unidades de entrada e saída nos menus suspensos, e digitar o número. O resultado será exibido em tempo real.' 
    },
    { 
      question: isEN ? 'How many converters are available on the platform?' : 'Quantos conversores existem na plataforma?', 
      answer: isEN ? `We support over ${totalConverters} detailed converters covering common measures (weight, length, area) to advanced measurements (electricity, radiology, fluid mechanics).` : `Oferecemos suporte a mais de ${totalConverters} conversores detalhados que abrangem desde medidas comuns (peso, comprimento, área) até medições avançadas (eletricidade, radiologia, mecânica dos fluidos).` 
    },
    { 
      question: isEN ? 'Are the tools completely free?' : 'As ferramentas são realmente gratuitas?', 
      answer: isEN ? 'Yes. None of our calculators have usage limits or costs.' : 'Sim. Nenhuma de nossas calculadoras possui limite de uso ou custo.' 
    },
    { 
      question: isEN ? 'What is the precision of the conversions?' : 'Qual a precisão das conversões?', 
      answer: isEN ? 'Our calculations use JavaScript floating-point numbers based on unified International System (SI) guidelines, providing high reliability in decimals.' : 'Nossos cálculos adotam números de ponto flutuante em JavaScript baseados em diretrizes unificadas do Sistema Internacional (SI), fornecendo alta confiabilidade na casa dos decimais.' 
    },
    { 
      question: isEN ? 'Does the conversion calculator work on mobile devices?' : 'A calculadora de conversão funciona em dispositivos móveis?', 
      answer: isEN ? 'Certainly, the entire interface is responsively adjusted to be quickly accessed and used on any mobile screen.' : 'Certamente, toda a interface é ajustada responsivamente para ser acessada e utilizada de maneira rápida na tela de qualquer celular.' 
    }
  ];

  const faqSchema = generateFAQSchema(faqs);

  const popularConverters = [
    { id: 'length', name: isEN ? 'Length' : 'Comprimento', desc: isEN ? 'm, km, miles, feet' : 'm, km, milhas, pés' },
    { id: 'weight', name: isEN ? 'Weight & Mass' : 'Peso e Massa', desc: isEN ? 'kg, lb, grams, oz' : 'kg, lb, gramas, oz' },
    { id: 'temperature', name: isEN ? 'Temperature' : 'Temperatura', desc: isEN ? 'Celsius, Fahrenheit, K' : 'Celsius, Fahrenheit, K' },
    { id: 'volume', name: isEN ? 'Volume' : 'Volume', desc: isEN ? 'Liters, gallons, m³' : 'Litros, galões, m³' },
    { id: 'speed', name: isEN ? 'Speed' : 'Velocidade', desc: isEN ? 'km/h, mph, knots' : 'km/h, mph, nós' },
    { id: 'area', name: isEN ? 'Area' : 'Área', desc: isEN ? 'm², hectares, acres' : 'm², hectares, acres' },
    { id: 'voltage', name: isEN ? 'Electric Voltage' : 'Tensão Elétrica', desc: isEN ? 'V, mV, kV' : 'V, mV, kV' },
    { id: 'aceleracao', name: isEN ? 'Acceleration' : 'Aceleração', desc: isEN ? 'm/s², km/h²' : 'm/s², km/h²' },
    { id: 'density', name: isEN ? 'Density' : 'Densidade', desc: isEN ? 'kg/m³, g/cm³' : 'kg/m³, g/cm³' },
    { id: 'time', name: isEN ? 'Time' : 'Tempo', desc: isEN ? 'Seconds, Hours, Days' : 'Segundos, Horas, Dias' }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEOHead 
        title={title}
        description={description}
        keywords="converter unidades, conversor online, conversor de medidas, transformar unidades"
        canonicalUrl="https://tudoconversor.com.br/"
        schema={combinedSchema}
        breadcrumbSchema={breadcrumbSchema}
        faqSchema={faqSchema}
        ogTitle={title}
        ogDescription={description}
      />
      
      <Header />

      <main id="main-content" className="flex-1 flex flex-col focus:outline-none" tabIndex="-1">
        <div className="py-12 md:py-16 flex-1">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10 text-center md:text-left">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-balance mb-6">{h1}</h1>
            <p className="text-xl text-muted-foreground leading-relaxed max-w-[80ch] mx-auto md:mx-0">
              {content}
            </p>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10" id="ferramenta">
            <ConverterApp />
          </div>

          <div className="px-4 py-4 sm:px-6 lg:px-8 border-b border-border/40 bg-muted/10 mb-12">
            <AdSense adFormat="horizontal" className="my-3" />
          </div>

          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-14" aria-labelledby="categorias-destaque">
            <h2 id="categorias-destaque" className="text-3xl font-extrabold tracking-tight mb-2 text-balance">{isEN ? 'Featured Categories' : 'Categorias em Destaque'}</h2>
            <p className="text-muted-foreground mb-8 max-w-[75ch]">{isEN ? 'Browse every conversion tool organized by area of knowledge.' : 'Navegue por todas as ferramentas de conversão organizadas por área do conhecimento.'}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {categories.map((category) => {
                const Icon = categoryIcons[category.id] || Boxes;
                const count = category.converters.length;
                return (
                  <Link
                    key={category.id}
                    to={isEN ? `/en/categoria/${category.id}/` : `/categoria/${category.id}/`}
                    className="group flex flex-col items-start gap-3 p-5 rounded-2xl border border-border/50 bg-card hover:bg-muted hover:border-primary/40 transition-all duration-200 shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={`${category.name} — ${count} ${isEN ? 'converters' : 'conversores'}`}
                  >
                    <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <span className="block font-semibold text-foreground group-hover:text-primary transition-colors">{category.name}</span>
                      <span className="block text-xs text-muted-foreground mt-1">
                        {count} {isEN ? (count === 1 ? 'converter' : 'converters') : (count === 1 ? 'conversor' : 'conversores')}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>

          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-14" aria-labelledby="sobre-destaque">
            <div className="bg-muted/30 border border-border/50 rounded-2xl p-8 md:p-10">
              <h2 id="sobre-destaque" className="text-2xl md:text-3xl font-bold mt-0 mb-4 text-balance">{isEN ? 'About TudoConversor' : 'Sobre o TudoConversor'}</h2>
              <p className="text-base text-muted-foreground leading-relaxed max-w-[80ch] mb-4">
                {isEN
                  ? 'TudoConversor is an independent web tool designed to quickly convert units of measurement across various physical quantities — from everyday basics like weight and length to currency with live exchange rates. Every conversion factor follows the International System of Units (SI) and BIPM standards, ensuring consistent, scientifically validated results.'
                  : 'O TudoConversor é uma ferramenta web independente, projetada para converter rapidamente unidades de medida de diversas grandezas — do básico do dia a dia, como peso e comprimento, até moeda com cotações em tempo real. Cada fator de conversão segue o Sistema Internacional de Unidades (SI) e os padrões do BIPM, garantindo resultados consistentes e cientificamente validados.'}
              </p>
              <p className="text-base text-muted-foreground leading-relaxed max-w-[80ch] mb-0">
                {isEN
                  ? 'All tools are free, run directly in your browser, and are available in both Portuguese and English — for students, professionals, and everyday use.'
                  : 'Todas as ferramentas são gratuitas, funcionam direto no seu navegador e estão disponíveis em português e inglês — para estudantes, profissionais e uso do dia a dia.'}
              </p>
              <Link to={isEN ? '/en/sobre/' : '/sobre/'} className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline mt-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm">
                {isEN ? 'Learn more' : 'Saiba mais'} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </section>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-10 items-start">
              
              <div className="xl:col-span-9 flex flex-col gap-14">
                
                <section className="bg-card border border-border rounded-2xl p-8 shadow-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <Zap className="w-6 h-6 text-primary fill-primary/10" />
                    <h2 className="text-2xl font-bold m-0">{isEN ? 'Top 10 Most Accessed Converters' : 'Top 10 Conversores Mais Acessados'}</h2>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                    {popularConverters.map(pc => (
                      <Link 
                        key={pc.id}
                        to={isEN ? `/en/conversor/${pc.id}/` : `/conversor/${pc.id}/`}
                        className="group flex flex-col p-4 rounded-xl border border-border/50 bg-muted/20 hover:bg-muted hover:border-primary/40 transition-all duration-200"
                      >
                        <span className="font-semibold text-foreground group-hover:text-primary transition-colors">{pc.name}</span>
                        <span className="text-xs text-muted-foreground mt-1">{pc.desc}</span>
                      </Link>
                    ))}
                  </div>
                </section>


                
                <section>
                  <h2 className="text-3xl font-extrabold tracking-tight mb-8 text-balance">{isEN ? `Explore Our ${totalConverters}+ Tools` : `Explore Nossas ${totalConverters}+ Ferramentas`}</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {categories.map((category) => (
                      <div key={category.id} className="bg-card border border-border rounded-2xl p-8 shadow-sm flex flex-col h-full">
                        <div className="flex items-center justify-between mb-6">
                          <h3 className="text-2xl font-bold m-0">{category.name}</h3>
                          <Link 
                            to={isEN ? `/en/categoria/${category.id}/` : `/categoria/${category.id}/`}
                            className="text-sm font-semibold text-primary hover:underline flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                            aria-label={`${isEN ? 'Explore Category' : 'Ver Categoria'} ${category.name}`}
                          >
                            {isEN ? 'Explore Category' : 'Ver Categoria'} <ArrowRight className="w-4 h-4" />
                          </Link>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-auto">
                          {category.converters.map(converter => (
                            <Link
                              key={converter.id}
                              to={isEN ? `/en/conversor/${converter.id}/` : `/conversor/${converter.id}/`}
                              className="inline-flex items-center px-4 py-2 rounded-lg bg-muted text-muted-foreground hover:bg-primary hover:text-primary-foreground text-sm font-medium transition-colors"
                            >
                              {converter.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="bg-muted/30 border border-border/50 rounded-2xl p-8">
                  <h2 className="text-2xl font-bold mt-0 mb-6 text-balance">{isEN ? 'Frequently Asked Questions' : 'Tire Suas Dúvidas Sobre o TudoConversor'}</h2>
                  <Accordion type="single" collapsible className="w-full">
                    {faqs.map((faq, index) => (
                      <AccordionItem key={index} value={`item-${index}`} className="border-border/50">
                        <AccordionTrigger className="text-left text-lg font-semibold hover:text-primary transition-colors">{faq.question}</AccordionTrigger>
                        <AccordionContent className="text-muted-foreground text-base leading-relaxed pt-2 pb-4">
                          {faq.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </section>

                <section className="bg-card border border-border rounded-2xl p-8 shadow-sm">
                  <h2 className="text-3xl font-extrabold tracking-tight mt-0 mb-6 text-balance">{isEN ? 'How Unit Conversion Works' : 'Como Funciona a Conversão de Unidades no TudoConversor'}</h2>
                  <div className="prose prose-slate dark:prose-invert max-w-none text-muted-foreground leading-relaxed text-base mb-10">
                    <p>
                      {isEN 
                        ? 'Our platform processes all mathematical calculations based on the rigorous standards established by the ' 
                        : 'Nossa plataforma processa todos os cálculos matemáticos com base nos rigorosos padrões estabelecidos pelo '}
                      <strong>{isEN ? 'International System of Units (SI)' : 'Sistema Internacional de Unidades (SI)'}</strong> 
                      {isEN ? ' and the ' : ' e pelo '}
                      <strong>{isEN ? 'International Bureau of Weights and Measures (BIPM)' : 'Bureau Internacional de Pesos e Medidas (BIPM)'}</strong>.
                      {isEN 
                        ? ' This ensures that every physical quantity conversion offers consistent, exact, and scientifically validated results for academic, school, and professional use worldwide.' 
                        : ' Isso garante que cada conversão de grandeza física ofereça resultados consistentes, exatos e cientificamente validados para uso acadêmico, escolar e profissional no Brasil e no mundo.'}
                    </p>
                    <p>
                      {isEN 
                        ? 'Unit conversion is a daily necessity in thousands of real-world scenarios: engineers calculating material resistance, travelers checking temperatures abroad, drivers tracking fuel consumption, and IT professionals provisioning server space. We eliminate the need to memorize complex multiplication and division factors by automating the entire process right in your browser.' 
                        : 'A conversão de unidades é uma necessidade diária em milhares de cenários reais: engenheiros calculando a resistência de materiais, viajantes verificando a temperatura em países estrangeiros, motoristas controlando o consumo de combustível e profissionais de tecnologia provisionando espaço em servidores. Nós eliminamos a necessidade de decorar fórmulas complexas de multiplicação e divisão de fatores, automatizando todo o processo no seu navegador.'}
                    </p>
                    <p>
                      {isEN
                        ? 'What makes TudoConversor different from a generic calculator is the combination of (1) pair-specific pages with formulas and reference tables, (2) category hubs that group related quantities, and (3) original educational articles on the history and definitions of units — not just a blank form. Currency is handled separately with live rates; temperature and logarithmic scales use dedicated formulas instead of a single factor.'
                        : 'O que diferencia o TudoConversor de uma calculadora genérica é a combinação de (1) páginas por par de unidades com fórmulas e tabelas de referência, (2) hubs de categoria que agrupam grandezas relacionadas e (3) artigos educativos originais sobre história e definição das unidades — não apenas um formulário em branco. Moeda é tratada à parte com cotações ao vivo; temperatura e escalas logarítmicas usam fórmulas dedicadas em vez de um único fator.'}
                    </p>
                    <p>
                      {isEN
                        ? 'We maintain bilingual PT/EN navigation, clear privacy and cookie notices, and contact channels so corrections and new-unit requests reach a real editorial owner. The goal is a trustworthy utility people return to — not a doorway farm of near-duplicate pages without explanation.'
                        : 'Mantemos navegação bilíngue PT/EN, avisos claros de privacidade e cookies, e canais de contato para que correções e pedidos de novas unidades cheguem a um responsável editorial real. O objetivo é uma utilidade confiável à qual as pessoas voltam — não uma fazenda de páginas quase idênticas sem explicação.'}
                    </p>
                  </div>

                  <h3 className="text-xl font-bold mb-5">{isEN ? 'Most Searched Conversions' : 'Conversões Mais Buscadas no Brasil'}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Link to={isEN ? '/en/conversor/length/mile-to-kilometer/' : '/conversor/length/milha-para-quilometro/'} className="flex items-center gap-2 p-3 rounded-xl border border-border/50 bg-muted/30 hover:bg-primary/5 hover:border-primary/30 hover:text-primary transition-all text-sm font-medium group">
                      {isEN ? 'Miles to km' : 'Milhas para km'} <ArrowRight className="w-4 h-4 ml-auto text-muted-foreground group-hover:text-primary transition-colors" />
                    </Link>
                    <Link to={isEN ? '/en/conversor/weight/kilogram-to-pound/' : '/conversor/weight/quilograma-para-libra/'} className="flex items-center gap-2 p-3 rounded-xl border border-border/50 bg-muted/30 hover:bg-primary/5 hover:border-primary/30 hover:text-primary transition-all text-sm font-medium group">
                      {isEN ? 'kg to Pounds' : 'kg para Libras'} <ArrowRight className="w-4 h-4 ml-auto text-muted-foreground group-hover:text-primary transition-colors" />
                    </Link>
                    <Link to={isEN ? '/en/conversor/temperature/celsius-to-fahrenheit/' : '/conversor/temperature/celsius-para-fahrenheit/'} className="flex items-center gap-2 p-3 rounded-xl border border-border/50 bg-muted/30 hover:bg-primary/5 hover:border-primary/30 hover:text-primary transition-all text-sm font-medium group">
                      {isEN ? '°C to °F' : '°C para °F'} <ArrowRight className="w-4 h-4 ml-auto text-muted-foreground group-hover:text-primary transition-colors" />
                    </Link>
                    <Link to={isEN ? '/en/conversor/volume/gallon-to-liter/' : '/conversor/volume/galao-para-litro/'} className="flex items-center gap-2 p-3 rounded-xl border border-border/50 bg-muted/30 hover:bg-primary/5 hover:border-primary/30 hover:text-primary transition-all text-sm font-medium group">
                      {isEN ? 'Gallons to Liters' : 'Galões para Litros'} <ArrowRight className="w-4 h-4 ml-auto text-muted-foreground group-hover:text-primary transition-colors" />
                    </Link>
                    <Link to={isEN ? '/en/conversor/area/hectare-to-square-meter/' : '/conversor/area/hectare-para-metro-quadrado/'} className="flex items-center gap-2 p-3 rounded-xl border border-border/50 bg-muted/30 hover:bg-primary/5 hover:border-primary/30 hover:text-primary transition-all text-sm font-medium group">
                      {isEN ? 'Hectares to m²' : 'Hectares para m²'} <ArrowRight className="w-4 h-4 ml-auto text-muted-foreground group-hover:text-primary transition-colors" />
                    </Link>
                    <Link to={isEN ? '/en/conversor/speed/mile-per-hour-to-kilometer-per-hour/' : '/conversor/speed/milha-por-hora-para-quilometro-por-hora/'} className="flex items-center gap-2 p-3 rounded-xl border border-border/50 bg-muted/30 hover:bg-primary/5 hover:border-primary/30 hover:text-primary transition-all text-sm font-medium group">
                      {isEN ? 'mph to km/h' : 'mph para km/h'} <ArrowRight className="w-4 h-4 ml-auto text-muted-foreground group-hover:text-primary transition-colors" />
                    </Link>
                    <Link to={isEN ? '/en/conversor/weight/pound-to-kilogram/' : '/conversor/weight/libra-para-quilograma/'} className="flex items-center gap-2 p-3 rounded-xl border border-border/50 bg-muted/30 hover:bg-primary/5 hover:border-primary/30 hover:text-primary transition-all text-sm font-medium group">
                      {isEN ? 'Pounds to kg' : 'Libras para kg'} <ArrowRight className="w-4 h-4 ml-auto text-muted-foreground group-hover:text-primary transition-colors" />
                    </Link>
                    <Link to={isEN ? '/en/conversor/energy/kilowatt-hour-to-kilojoule/' : '/conversor/energy/quilowatt-hora-para-quilojoule/'} className="flex items-center gap-2 p-3 rounded-xl border border-border/50 bg-muted/30 hover:bg-primary/5 hover:border-primary/30 hover:text-primary transition-all text-sm font-medium group">
                      {isEN ? 'kWh to kJ' : 'kWh para kJ'} <ArrowRight className="w-4 h-4 ml-auto text-muted-foreground group-hover:text-primary transition-colors" />
                    </Link>
                  </div>
                </section>

                <section className="bg-muted/20 border border-border/50 rounded-2xl p-6">
                  <ShareButtons className="justify-center sm:justify-between" />
                </section>

              </div>

              <aside className="hidden xl:block xl:col-span-3 sticky top-28">
                <AdSense adFormat="vertical" />
              </aside>
              
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default HomePage;